import React from "react"
import { useTranslations } from "next-intl"

import { formatPrice } from "@/lib/helper"
import { generateBasePriceInfo } from "@/lib/price-utils"
import { BasketLineItemData, BasketPromotionCode } from "@/lib/storefront/basket/basket"
import { cn } from "@/components/ui/helpers/utils"

export function BasketLineItemPriceInfo(props: {
  isB2B: boolean
  isoLocale: string
  lineItem: BasketLineItemData
  basketPromotionCodes?: BasketPromotionCode[]
  basketCartDiscountEnabled?: boolean
  showAllPrices?: boolean
}) {
  const t = useTranslations("storefront")
  const hasPromotionCode = (props.basketPromotionCodes?.length ?? 0) > 0
  const showBasketCartDiscountLabel = props.basketCartDiscountEnabled && !hasPromotionCode
  const hasPrimaryDiscount = (props.lineItem.discountAmount ?? 0) > 0.009
  const hasCartDiscount = showBasketCartDiscountLabel && (props.lineItem.cartDiscountAmount ?? 0) > 0.009
  const showAdditionalCartDiscount = hasCartDiscount && hasPrimaryDiscount && props.lineItem.discountType !== "VOUCHER"
  const showStandaloneCartDiscount =
    hasCartDiscount && !showAdditionalCartDiscount && !(props.lineItem.discountType === "VOUCHER" && hasPrimaryDiscount)
  const cartDiscountBaseNet = (props.lineItem.totalListPriceNet ?? 0) - props.lineItem.discountAmount
  const currency =
    props.lineItem.product.currency ?? props.lineItem.originalLineItem.pricing?.price?.net?.currency ?? "EUR"

  const pricePerUnitText = generateBasePriceInfo(
    props.isoLocale,
    props.lineItem.product,
    props.isB2B ? props.lineItem.singleSalePriceNet : props.lineItem.singleSalePriceGross
  )

  return (
    <div
      className={cn("flex h-full flex-col justify-between gap-1", {
        "justify-between": !props.isB2B,
      })}
    >
      {props.isB2B || props.showAllPrices ? (
        <div className={"flex flex-col justify-start gap-1"}>
          <div className={"flex flex-col items-end justify-start"}>
            <div
              className={cn("hidden flex-row justify-end gap-1 text-xs", {
                flex: props.lineItem.isDiscounted,
              })}
            >
              <p className={"min-w-24 text-right font-bold"}>{t("basket.add_dialog.normal")}:</p>
              <p
                className={cn("min-w-24 text-right decoration-primary", {
                  "line-through": props.lineItem.isDiscounted,
                })}
              >
                {props.lineItem.totalListPriceFormatted}
              </p>
            </div>
            <div
              className={cn("hidden flex-row justify-end gap-1 text-xs", {
                flex: hasPrimaryDiscount,
              })}
            >
              {(() => {
                switch (props.lineItem.discountType) {
                  /*Preisliste*/

                  case "PRICELIST":
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount", {
                          discountPercentFormatted: props.lineItem.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                  /*Rahmenrabatt*/
                  case "GENERAL":
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount_general", {
                          discountPercentFormatted: props.lineItem.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                  /*Gutschein*/
                  case "VOUCHER":
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t(
                          showBasketCartDiscountLabel
                            ? "basket.add_dialog.discount_cart"
                            : "basket.add_dialog.discount_voucher",
                          {
                            discountPercentFormatted: props.lineItem.discountPercentFormatted,
                          }
                        )}
                        :
                      </p>
                    )
                  /*Sonderrabatt Kategorie*/
                  case "CATEGORY":
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount_category", {
                          discountPercentFormatted: props.lineItem.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                  /*Sonderrabatt Kategorie*/
                  default:
                    return (
                      <p className={"min-w-24 text-right font-bold text-green-500"}>
                        {t("basket.add_dialog.discount_category", {
                          discountPercentFormatted: props.lineItem.discountPercentFormatted,
                        })}
                        :
                      </p>
                    )
                }
              })()}

              <p className={"min-w-24 text-right"}>{props.lineItem.discountAmountFormatted}</p>
            </div>
            {showStandaloneCartDiscount && (
              <div className={"flex flex-row justify-end gap-1 text-xs"}>
                <p className={"min-w-24 text-right font-bold text-green-500"}>
                  {t("basket.add_dialog.discount_cart", {
                    discountPercentFormatted: props.lineItem.cartDiscountPercentFormatted,
                  })}
                  :
                </p>
                <p className={"min-w-24 text-right"}>{props.lineItem.cartDiscountAmountFormatted}</p>
              </div>
            )}
            {showAdditionalCartDiscount && (
              <>
                <div className={"flex flex-row justify-end gap-1 text-xs"}>
                  <p className={"min-w-24 text-right"}>{t("basket.add_dialog.subtotal")}:</p>
                  <p className={"min-w-24 text-right"}>{formatPrice(props.isoLocale, cartDiscountBaseNet, currency)}</p>
                </div>
                <div className={"flex flex-row justify-end gap-1 text-xs"}>
                  <p className={"min-w-24 text-right font-bold text-green-500"}>
                    {t("basket.add_dialog.discount_cart", {
                      discountPercentFormatted: props.lineItem.cartDiscountPercentFormatted,
                    })}
                    :
                  </p>
                  <p className={"min-w-24 text-right"}>{props.lineItem.cartDiscountAmountFormatted}</p>
                </div>
              </>
            )}

            <div
              className={cn("hidden flex-row justify-end gap-1 text-xs", {
                flex:
                  (props.lineItem.ecoTax ?? 0) > 0 &&
                  props.lineItem.ecoTaxFormatted &&
                  props.lineItem.ecoTaxFormatted !== "",
              })}
            >
              <p className={"min-w-24 text-right"}>{t("basket.add_dialog.eco_tax")}:</p>
              <p className={"min-w-24 text-right"}>{props.lineItem.ecoTaxFormatted}</p>
            </div>
            <div className={"flex flex-row justify-between gap-1 text-xs"}>
              <p className={"min-w-24 text-right"}>{t("basket.add_dialog.single")}:</p>
              <p className={"min-w-24 text-right"}>{props.lineItem.salePriceFormatted}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className={"flex flex-col justify-start gap-1"}>
          <div className={"flex flex-col items-end justify-start"}>
            <div className={"flex flex-row justify-between gap-1 text-xs"}>
              <p className={"min-w-24 text-right"}>{t("basket.add_dialog.single")}:</p>
              <p className={"min-w-24 text-right"}>{props.lineItem.salePriceFormatted}</p>
            </div>
          </div>
        </div>
      )}

      <div>
        {!props.isB2B && props.lineItem.isDiscounted && (
          <div className={"flex flex-row justify-end gap-1"}>
            {/*Aktionspreis*/}
            {props.lineItem.discountType === "VOUCHER" ? (
              <p className={"text-xs font-bold text-nowrap text-primary"}>{t("product.special_offer_voucher")}</p>
            ) : (
              <p className={"text-xs font-bold text-nowrap text-primary"}>{t("product.special_offer")}</p>
            )}
          </div>
        )}
        {props.lineItem.originalProduct?.isDoNotPromoteAll && (
          <div className={"flex flex-row justify-end gap-1 text-xs"}>
            <span>
              <span className={"text-xs font-bold text-primary"}>{t("product.best_price")}</span>
              <span className={"text-xs font-normal"}> - {t("product.do_not_promote_all")}</span>
            </span>
          </div>
        )}
        <div className={"flex flex-row items-center justify-end gap-1 text-sm"}>
          <p className={"min-w-24 text-right"}>{t("basket.add_dialog.sum")}:</p>
          <p className={"min-w-24 text-right text-lg font-bold text-primary"}>
            {props.lineItem.totalSalePriceFormatted}
          </p>
        </div>
        {props.lineItem.product.packagingUnitInfo && props.lineItem.product.packagingUnitInfo !== "" && (
          <div className={"flex flex-row items-center justify-end gap-1 text-sm"}>
            <p className={"w-full min-w-24 text-right text-xs"}>{props.lineItem.product.packagingUnitInfo ?? ""}</p>
          </div>
        )}

        {pricePerUnitText && pricePerUnitText !== "" && (
          <div className={"flex flex-row items-center justify-end gap-1 text-sm"}>
            <p className={"w-full min-w-24 text-right text-xs"}>{pricePerUnitText ?? ""}</p>
          </div>
        )}
      </div>
    </div>
  )
}
